/* ═══════════════════════════════════════════════════════════════
   UNIVERSAL UNDO — one timeline across every domain of a workspace.

   Josh's ruling (docs/DESIGNER_UNIFIED_PLAN.md §2.7): Ctrl+Z always
   undoes the LAST ACTION, whatever it was — text change, token move,
   zone edit, level-style change, delete, add. The user must never
   have to think about which "kind" of undo they need.

   The mechanism is deliberately small: each domain keeps its OWN
   history (ProseMirror's for the document, the forma draft's snapshot
   stack for formatting). This coordinator only records the ORDER the
   actions happened in, and dispatches Ctrl+Z / Ctrl+Y to whichever
   domain owns the most recent step.

   Two rules make it correct:
     · while the coordinator is driving an undo/redo it sets
       `applying` — a domain reacting to its own reversal must not
       record that reversal as a new action;
     · if the domain that owns the top entry has nothing left to undo
       (its own history was trimmed or rebuilt), the entry is stale:
       drop it and try the next one, so Ctrl+Z never no-ops while
       there is still something to undo.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';

/** The histories the coordinator arbitrates between. */
export type UndoDomain = 'doc' | 'forma';

export interface DomainHandlers {
  /** Undo one step. Return false when the domain had nothing to undo. */
  undo: () => boolean;
  /** Redo one step. Return false when the domain had nothing to redo. */
  redo: () => boolean;
}

export interface UndoCoordinator {
  /** A domain performed a fresh USER action. Stable identity. */
  note: (domain: UndoDomain) => void;
  /** Wire (or unwire) a domain's undo/redo. Stable identity. */
  register: (domain: UndoDomain, handlers: DomainHandlers | null) => void;
  /** Undo the last action, whichever domain it happened in. */
  undo: () => void;
  /** Redo the last undone action. */
  redo: () => void;
  /** Throw the timeline away (a new document is a new timeline). */
  reset: () => void;
  /** True while an undo/redo is being applied — domains check this so they
   *  don't record the reversal as a new action. */
  isApplying: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
}

/** Deep enough to cover a long working session; the per-domain histories
 *  have their own (smaller) caps, and stale entries are skipped anyway. */
const TIMELINE_LIMIT = 400;

export function useUndoCoordinator(): UndoCoordinator {
  const undoRef = React.useRef<UndoDomain[]>([]);
  const redoRef = React.useRef<UndoDomain[]>([]);
  const handlersRef = React.useRef<Partial<Record<UndoDomain, DomainHandlers>>>({});
  const applyingRef = React.useRef(false);
  const [, tick] = React.useReducer((x: number) => x + 1, 0);

  const isApplying = React.useCallback(() => applyingRef.current, []);

  const note = React.useCallback((domain: UndoDomain) => {
    if (applyingRef.current) return;
    undoRef.current.push(domain);
    if (undoRef.current.length > TIMELINE_LIMIT) undoRef.current.shift();
    // A fresh action forks the timeline — everything that was undone is gone,
    // exactly as it is in Word.
    if (redoRef.current.length) redoRef.current = [];
    tick();
  }, []);

  const register = React.useCallback((domain: UndoDomain, handlers: DomainHandlers | null) => {
    if (handlers) handlersRef.current[domain] = handlers;
    else delete handlersRef.current[domain];
  }, []);

  /** Pop entries off `from` until one of them actually moves, pushing the
   *  one that worked onto `to`. Stale entries (a domain whose own history no
   *  longer has that step) are discarded rather than swallowing the keypress. */
  const step = React.useCallback(
    (
      from: React.MutableRefObject<UndoDomain[]>,
      to: React.MutableRefObject<UndoDomain[]>,
      which: 'undo' | 'redo'
    ) => {
      if (applyingRef.current) return;
      applyingRef.current = true;
      try {
        while (from.current.length) {
          const domain = from.current.pop() as UndoDomain;
          const handler = handlersRef.current[domain];
          if (handler && handler[which]()) {
            to.current.push(domain);
            return;
          }
        }
      } finally {
        applyingRef.current = false;
        tick();
      }
    },
    []
  );

  const undo = React.useCallback(() => step(undoRef, redoRef, 'undo'), [step]);
  const redo = React.useCallback(() => step(redoRef, undoRef, 'redo'), [step]);

  const reset = React.useCallback(() => {
    undoRef.current = [];
    redoRef.current = [];
    tick();
  }, []);

  return {
    note,
    register,
    undo,
    redo,
    reset,
    isApplying,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
  };
}
